import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import StaffSectionLayout from '../../../components/staff/StaffSectionLayout';
import ContentBox from '../../../components/ContentBox';
import { FpButton, FpModal, StatusPill, SetArt } from '../../../components/frankopop/frankopopShared';
import SetConfigForm from './SetConfigForm';
import { useFrankopopData, fetchAdminSets, adminCreateSet, adminDeleteSet, CURRENCY } from '../../../data/frankopop';

function SetRow({ set, onDeleted }) {
    const navigate = useNavigate();

    const del = async () => {
        const res = await adminDeleteSet(set.id);
        if (!res.success) toast.error(res.message);
        else { toast.success('Set deleted.'); onDeleted(); }
    };

    return (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 border border-gray-200 bg-white p-3" style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.08)' }}>
            <SetArt set={set} size={56} />
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-['Jersey_10'] uppercase text-[16px] tracking-[1px] text-gray-900">{set.name}</span>
                    <StatusPill status={set.status} />
                    <span className="font-['Jersey_10'] uppercase text-[10px] tracking-[1px] px-1.5 py-0.5 bg-gray-200 text-gray-600">
                        {set.giftableOnly ? `Gift · ${set.giftPermission === 'admins_only' ? 'admins' : 'staff'}` : 'Shop'}
                    </span>
                    {set.contentsLocked && (
                        <span className="font-['Jersey_10'] uppercase text-[10px] tracking-[1px] px-1.5 py-0.5 bg-amber-200 text-amber-800">Contents locked</span>
                    )}
                </div>
                <p className="text-[12px] text-gray-500 font-['Jersey_10'] uppercase tracking-[1px] mt-1">
                    {set.figureCount} FrankoPops
                    {!set.giftableOnly && ` · ${set.priceCards} ${CURRENCY}`}
                    {set.limitedRun && ` · ${set.boxesSold}/${set.limitedRunTotal} sold`}
                    {set.giftableOnly && ` · ${set.boxesTotal} gifted`}
                </p>
            </div>
            <div className="flex gap-2 shrink-0">
                <FpButton size="sm" variant="ghost" onClick={() => navigate(`/staff/frankopop/sets/${set.id}`)}>Edit</FpButton>
                <FpButton size="sm" variant="danger" disabled={!!set.firstReleasedAt || set.boxesTotal > 0} onClick={del}>Delete</FpButton>
            </div>
        </div>
    );
}

export default function FrankoPopSetsAdmin() {
    const { data: sets, loading, refetch } = useFrankopopData(useCallback(() => fetchAdminSets(), []), []);
    const [creating, setCreating] = useState(false);
    const navigate = useNavigate();

    const create = async (payload) => {
        const res = await adminCreateSet(payload);
        if (!res.success) { toast.error(res.message); return; }
        setCreating(false);
        toast.success('Set created - now add its FrankoPops.');
        navigate(`/staff/frankopop/sets/${res.id}`);
    };

    return (
        <StaffSectionLayout>
            <ContentBox
                theme="default"
                tint="red"
                header="FrankoPop - sets"
                headerRight={<FpButton size="sm" variant="gold" onClick={() => setCreating(true)}>Create set</FpButton>}
            >
                <p className="text-sm text-gray-600 mb-4">
                    Admins configure sets here. A set&apos;s <strong>contents lock permanently</strong> once it goes on
                    sale (shop sets) or the first box is gifted (giftable-only sets) - players may already own boxes,
                    so the odds and figures can&apos;t change after that.
                </p>

                {loading ? (
                    <p className="text-sm text-gray-500">Loading sets...</p>
                ) : (
                    <div className="space-y-2">
                        {(sets || []).map((set) => <SetRow key={set.id} set={set} onDeleted={refetch} />)}
                    </div>
                )}
            </ContentBox>

            {creating && (
                <FpModal title="Create a FrankoPop set" onClose={() => setCreating(false)} maxWidth="max-w-2xl">
                    <SetConfigForm onSubmit={create} onCancel={() => setCreating(false)} submitLabel="Create set" />
                </FpModal>
            )}

            <p className="mt-3 text-center text-[12px] text-gray-400 font-['Jersey_10'] uppercase tracking-[1px]">
                <Link to="/staff/frankopop/rarities" className="underline hover:text-maroon">Manage rarity types →</Link>
            </p>
        </StaffSectionLayout>
    );
}
